import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Mail, ShieldCheck, BarChart3, FlaskConical, Wallet, Sparkles,
  Cloud, LineChart, Bell, CreditCard, Network, Compass, Workflow, Code2,
  Boxes, Layers, Rocket, Building2, Target, Users, Lock, Zap, Check,
  MessageSquare, Fingerprint, Activity, TrendingUp, Search,
} from "lucide-react";

// ---------- Premium in-app SVG mockups (brand-consistent, no external assets) ----------
const MOCKUPS = {
  quixia: (
    <svg viewBox="0 0 480 340" className="h-full w-full">
      <defs>
        <linearGradient id="qxbg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a0a0a" /><stop offset="1" stopColor="#141414" />
        </linearGradient>
        <linearGradient id="qxbar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#00e5ff" stopOpacity="0.95" /><stop offset="1" stopColor="#00e5ff" stopOpacity="0.35" />
        </linearGradient>
      </defs>
      <rect width="480" height="340" rx="12" fill="url(#qxbg)" stroke="#232323" />
      {/* header */}
      <rect x="14" y="14" width="452" height="30" rx="6" fill="#141414" stroke="#232323" />
      <circle cx="30" cy="29" r="4" fill="#00e5ff" />
      <text x="42" y="33" fontFamily="monospace" fontSize="10" fill="#e5e5e5">quixia · marketing intelligence</text>
      <rect x="380" y="20" width="70" height="18" rx="9" fill="#00e5ff22" stroke="#00e5ff55" />
      <text x="415" y="32" fontFamily="monospace" fontSize="8" fill="#00e5ff" textAnchor="middle">LIVE · Q4</text>
      {/* KPI row */}
      {[
        { x: 14, label: "ROAS", val: "4.8×", color: "#00e5ff" },
        { x: 130, label: "CTR", val: "3.2%", color: "#ccff00" },
        { x: 246, label: "CAC", val: "$18", color: "#00e5ff" },
        { x: 362, label: "LTV", val: "$412", color: "#ccff00" },
      ].map((k, i) => (
        <g key={i}>
          <rect x={k.x} y="56" width="104" height="52" rx="8" fill="#141414" stroke="#232323" />
          <text x={k.x + 10} y="72" fontFamily="monospace" fontSize="8" fill="#8c8c8c">{k.label}</text>
          <text x={k.x + 10} y="98" fontFamily="Cabinet Grotesk, Inter, sans-serif" fontWeight="900" fontSize="22" fill={k.color}>{k.val}</text>
        </g>
      ))}
      {/* Bar chart */}
      <rect x="14" y="122" width="288" height="200" rx="8" fill="#141414" stroke="#232323" />
      <text x="26" y="142" fontFamily="monospace" fontSize="9" fill="#00e5ff">// channel performance</text>
      {[
        { x: 30,  h: 90 }, { x: 70,  h: 130 }, { x: 110, h: 60 }, { x: 150, h: 145 }, { x: 190, h: 100 }, { x: 230, h: 120 }, { x: 270, h: 155 },
      ].map((b, i) => (
        <rect key={i} x={b.x} y={310 - b.h} width="22" height={b.h} rx="3" fill="url(#qxbar)" />
      ))}
      <line x1="20" y1="310" x2="290" y2="310" stroke="#333" />
      {/* Right column: recommendations */}
      <rect x="314" y="122" width="152" height="200" rx="8" fill="#141414" stroke="#232323" />
      <text x="326" y="142" fontFamily="monospace" fontSize="9" fill="#ccff00">// next-best actions</text>
      {[
        "Boost budget on Meta +25%",
        "Pause 3 losing keywords",
        "Re-engage 412 churn risks",
      ].map((t, i) => (
        <g key={i}>
          <circle cx="330" cy={165 + i * 42} r="4" fill="#ccff00" />
          <text x="342" y={169 + i * 42} fontFamily="Inter, sans-serif" fontSize="10" fill="#e5e5e5">{t}</text>
        </g>
      ))}
    </svg>
  ),
  vaultxy: (
    <svg viewBox="0 0 480 340" className="h-full w-full">
      <defs>
        <linearGradient id="vxbg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a0a0a" /><stop offset="1" stopColor="#141414" />
        </linearGradient>
      </defs>
      <rect width="480" height="340" rx="12" fill="url(#vxbg)" stroke="#232323" />
      <rect x="14" y="14" width="452" height="30" rx="6" fill="#141414" stroke="#232323" />
      <text x="24" y="33" fontFamily="monospace" fontSize="10" fill="#e5e5e5">vaultxy · security posture</text>
      <rect x="380" y="20" width="70" height="18" rx="9" fill="#ccff0022" stroke="#ccff0055" />
      <text x="415" y="32" fontFamily="monospace" fontSize="8" fill="#ccff00" textAnchor="middle">SECURE</text>
      {/* Score gauge */}
      <rect x="14" y="56" width="220" height="266" rx="8" fill="#141414" stroke="#232323" />
      <text x="26" y="76" fontFamily="monospace" fontSize="9" fill="#00e5ff">// posture score</text>
      <circle cx="124" cy="182" r="70" fill="none" stroke="#232323" strokeWidth="14" />
      <circle cx="124" cy="182" r="70" fill="none" stroke="#ccff00" strokeWidth="14" strokeDasharray="360 500" strokeLinecap="round" transform="rotate(-90 124 182)" />
      <text x="124" y="188" fontFamily="Cabinet Grotesk, Inter, sans-serif" fontWeight="900" fontSize="42" fill="#ccff00" textAnchor="middle">92</text>
      <text x="124" y="216" fontFamily="monospace" fontSize="9" fill="#8c8c8c" textAnchor="middle">/ 100 · STRONG</text>
      <text x="124" y="284" fontFamily="Inter" fontSize="9" fill="#e5e5e5" textAnchor="middle">3 findings resolved</text>
      <text x="124" y="298" fontFamily="Inter" fontSize="9" fill="#8c8c8c" textAnchor="middle">this week</text>
      {/* Alerts list */}
      <rect x="246" y="56" width="220" height="266" rx="8" fill="#141414" stroke="#232323" />
      <text x="258" y="76" fontFamily="monospace" fontSize="9" fill="#00e5ff">// active findings · 4</text>
      {[
        { sev: "high",   c: "#f87171", t: "Exposed S3 bucket · web-assets" },
        { sev: "med",    c: "#facc15", t: "Stale IAM token · analytics-svc" },
        { sev: "med",    c: "#facc15", t: "MFA missing · 2 admin users" },
        { sev: "low",    c: "#22d3ee", t: "Weak cipher · legacy API" },
      ].map((a, i) => (
        <g key={i}>
          <rect x="258" y={94 + i * 50} width="196" height="40" rx="6" fill="#0a0a0a" stroke="#232323" />
          <rect x="258" y={94 + i * 50} width="3" height="40" fill={a.c} />
          <text x="268" y={110 + i * 50} fontFamily="monospace" fontSize="7" fill={a.c}>{a.sev.toUpperCase()}</text>
          <text x="268" y={124 + i * 50} fontFamily="Inter" fontSize="9" fill="#e5e5e5">{a.t}</text>
        </g>
      ))}
    </svg>
  ),
  labs: (
    <svg viewBox="0 0 480 340" className="h-full w-full">
      <defs>
        <linearGradient id="lbg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a0a0a" /><stop offset="1" stopColor="#141414" />
        </linearGradient>
      </defs>
      <rect width="480" height="340" rx="12" fill="url(#lbg)" stroke="#232323" />
      <rect x="14" y="14" width="452" height="30" rx="6" fill="#141414" stroke="#232323" />
      <text x="24" y="33" fontFamily="monospace" fontSize="10" fill="#e5e5e5">labs · experiments board</text>
      {/* Kanban columns */}
      {[
        { x: 14,  title: "IDEAS",       color: "#8c8c8c", items: ["Voice-first math tutor", "Automated CI risk score", "Team OKR co-pilot"] },
        { x: 165, title: "PROTOTYPE",   color: "#00e5ff", items: ["Personalised newsletter", "Vendor risk scanner"] },
        { x: 316, title: "VALIDATING",  color: "#ccff00", items: ["Voice tutor beta", "OKR co-pilot pilot"] },
      ].map((col, ci) => (
        <g key={ci}>
          <rect x={col.x} y="56" width="136" height="266" rx="8" fill="#141414" stroke="#232323" />
          <text x={col.x + 12} y="76" fontFamily="monospace" fontSize="9" fill={col.color}>{`// ${col.title.toLowerCase()}`}</text>
          {col.items.map((it, ii) => (
            <g key={ii}>
              <rect x={col.x + 10} y={92 + ii * 60} width="116" height="50" rx="6" fill="#0a0a0a" stroke="#232323" />
              <rect x={col.x + 10} y={92 + ii * 60} width="3" height="50" fill={col.color} />
              <text x={col.x + 20} y={110 + ii * 60} fontFamily="Inter" fontSize="9" fill="#e5e5e5">
                {it.length > 22 ? it.slice(0, 22) + "…" : it}
              </text>
              <text x={col.x + 20} y={126 + ii * 60} fontFamily="monospace" fontSize="7" fill="#8c8c8c">
                {["Design", "Eng", "Research"][ii % 3]} · {["2d", "1w", "4d"][ii % 3]}
              </text>
            </g>
          ))}
        </g>
      ))}
    </svg>
  ),
  flexit: (
    <svg viewBox="0 0 480 340" className="h-full w-full">
      <defs>
        <linearGradient id="fbg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a0a0a" /><stop offset="1" stopColor="#141414" />
        </linearGradient>
        <linearGradient id="fcard" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#00e5ff" stopOpacity="0.9" /><stop offset="1" stopColor="#ccff00" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <rect width="480" height="340" rx="12" fill="url(#fbg)" stroke="#232323" />
      <rect x="14" y="14" width="452" height="30" rx="6" fill="#141414" stroke="#232323" />
      <text x="24" y="33" fontFamily="monospace" fontSize="10" fill="#e5e5e5">flexit · money co-pilot</text>
      {/* Card */}
      <rect x="14" y="56" width="220" height="130" rx="12" fill="url(#fcard)" />
      <text x="30" y="82" fontFamily="monospace" fontSize="8" fill="#0a0a0a">// balance · GBP</text>
      <text x="30" y="118" fontFamily="Cabinet Grotesk, Inter, sans-serif" fontWeight="900" fontSize="30" fill="#0a0a0a">£12,480.51</text>
      <text x="30" y="140" fontFamily="Inter" fontSize="10" fill="#0a0a0a">+£1,204 this month</text>
      <text x="30" y="172" fontFamily="monospace" fontSize="9" fill="#0a0a0a">•••• 4821</text>
      <text x="205" y="172" fontFamily="monospace" fontSize="9" fill="#0a0a0a" textAnchor="end">flexit</text>
      {/* Insight */}
      <rect x="246" y="56" width="220" height="130" rx="8" fill="#141414" stroke="#232323" />
      <text x="258" y="76" fontFamily="monospace" fontSize="9" fill="#ccff00">// this week&apos;s insight</text>
      <text x="258" y="102" fontFamily="Cabinet Grotesk, Inter, sans-serif" fontWeight="900" fontSize="18" fill="#e5e5e5">You can afford</text>
      <text x="258" y="124" fontFamily="Cabinet Grotesk, Inter, sans-serif" fontWeight="900" fontSize="18" fill="#00e5ff">£340 more</text>
      <text x="258" y="146" fontFamily="Inter" fontSize="10" fill="#8c8c8c">towards savings without</text>
      <text x="258" y="160" fontFamily="Inter" fontSize="10" fill="#8c8c8c">touching essentials.</text>
      <rect x="258" y="168" width="90" height="14" rx="7" fill="#00e5ff22" stroke="#00e5ff55" />
      <text x="303" y="178" fontFamily="monospace" fontSize="8" fill="#00e5ff" textAnchor="middle">MOVE NOW</text>
      {/* Transactions */}
      <rect x="14" y="200" width="452" height="122" rx="8" fill="#141414" stroke="#232323" />
      <text x="26" y="220" fontFamily="monospace" fontSize="9" fill="#00e5ff">// recent transactions</text>
      {[
        { d: "Wed",  t: "Salary · Studio Ltd",   a: "+£4,120.00", c: "#ccff00" },
        { d: "Tue",  t: "Groceries · Waitrose",  a: "-£68.20",    c: "#e5e5e5" },
        { d: "Mon",  t: "Transport · TfL",       a: "-£14.80",    c: "#e5e5e5" },
      ].map((r, i) => (
        <g key={i}>
          <text x="30"  y={244 + i * 22} fontFamily="monospace" fontSize="9" fill="#8c8c8c">{r.d}</text>
          <text x="80"  y={244 + i * 22} fontFamily="Inter" fontSize="10" fill="#e5e5e5">{r.t}</text>
          <text x="450" y={244 + i * 22} fontFamily="monospace" fontSize="10" fill={r.c} textAnchor="end">{r.a}</text>
          <line x1="30" y1={252 + i * 22} x2="450" y2={252 + i * 22} stroke="#232323" />
        </g>
      ))}
    </svg>
  ),
};

// ---------- Product configurations ----------
const PRODUCTS = {
  quixia: {
    name: "Quixia",
    tag: "Marketing Intelligence",
    icon: BarChart3,
    tagline: "See every signal. Make every campaign smarter.",
    lede: "Quixia unifies your marketing data across every channel and turns it into concrete next actions — not just charts. Understand your audience, decode intent, and know exactly what to do next.",
    problem: "Marketing teams drown in dashboards. Data lives in five tools, insights live in none, and campaigns get decided on gut feel.",
    solution: "Quixia connects every source — ads, email, web, social, CRM — into one intelligent workspace that translates raw data into 'do this next' recommendations.",
    features: [
      { icon: LineChart,   title: "Unified analytics",      body: "One dashboard across every channel — ads, email, social, web, CRM. No more tab-switching." },
      { icon: Search,      title: "Audience decoding",     body: "See who your best customers really are and where more of them live online." },
      { icon: TrendingUp,  title: "Campaign intelligence", body: "Automatic uplift analysis on every campaign so you know what worked and why." },
      { icon: Sparkles,    title: "Next-best action",      body: "Recommendations that translate data into concrete, ranked moves for the coming week." },
      { icon: Network,     title: "Channel connectors",    body: "Native integrations with the tools your marketing team already runs on." },
      { icon: Workflow,    title: "Automated playbooks",   body: "Turn insights into automated actions — re-engage churn risks, boost winners, pause losers." },
    ],
    useCases: [
      "In-house marketing teams at growing companies",
      "Agencies managing many client campaigns from one workspace",
      "eCommerce teams optimising ROAS across paid channels",
      "SaaS marketers tying pipeline back to source",
    ],
  },
  vaultxy: {
    name: "Vaultxy",
    tag: "Cybersecurity",
    icon: ShieldCheck,
    tagline: "Modern security, without the noise.",
    lede: "Vaultxy continuously watches your business — from code to cloud to identity — and tells you what actually matters, in plain English, with a clear next step.",
    problem: "Security tools flood teams with alerts. 99% are noise. The 1% that matter get missed.",
    solution: "Vaultxy consolidates monitoring, detection and response into a single platform that prioritises signals ruthlessly and turns each finding into an actionable playbook.",
    features: [
      { icon: Activity,   title: "Continuous monitoring", body: "24/7 visibility across your code, cloud, identity and third-party surface." },
      { icon: Fingerprint,title: "Identity protection",    body: "Detect unusual access, privilege drift and credential exposure across your entire org." },
      { icon: Lock,       title: "Compliance-ready",      body: "SOC 2, ISO 27001 and GDPR reports generated from the same data your engineers already produce." },
      { icon: Zap,        title: "Prioritised alerts",    body: "Ruthless deduplication and severity scoring. Only what matters, when it matters." },
      { icon: Code2,      title: "Code-to-cloud scanning",body: "Every commit checked. Every deployed asset watched. One view across the whole chain." },
      { icon: Workflow,   title: "Response playbooks",    body: "Guided remediation steps for every finding — your team never has to Google what to do next." },
    ],
    useCases: [
      "Scale-ups formalising security ahead of enterprise deals",
      "Fintech and healthtech teams navigating strict compliance",
      "Engineering-led orgs wanting DevSecOps without the friction",
      "Companies preparing for SOC 2 or ISO 27001 audits",
    ],
  },
  labs: {
    name: "LaneX Labs",
    tag: "Innovation Studio",
    icon: FlaskConical,
    tagline: "Where every next LaneX product is born.",
    lede: "LaneX Labs is our internal software studio — the place where ideas become prototypes, prototypes become validated products, and validated products graduate into new companies inside the LaneX ecosystem.",
    problem: "Great software companies get one thing right and never build a second thing. The muscle for shipping new products atrophies.",
    solution: "Labs is a dedicated studio inside LaneX Holdings with the design system, shared platform and small-team culture required to ship new products at a steady rhythm — every quarter, every year, for decades.",
    features: [
      { icon: Rocket,      title: "Rapid prototyping",  body: "Small pods spin up new product prototypes in weeks — not quarters — on our shared platform." },
      { icon: Layers,      title: "Shared design system", body: "Every experiment starts with the same premium components, so it looks like a product from day one." },
      { icon: Boxes,       title: "Common APIs",         body: "Identity, billing, analytics and communications are all one API call away — no wheel-reinvention." },
      { icon: Users,       title: "Customer validation",  body: "Every idea graduates only after real customers pay for it or actively use it — never before." },
      { icon: TrendingUp,  title: "Portfolio thinking",  body: "We treat product bets like a portfolio. Some go big. Some get killed. The muscle keeps growing." },
      { icon: Compass,     title: "Directional research", body: "Labs also incubates deeper research into how modern software should work — the next decade, not next quarter." },
    ],
    useCases: [
      "Every product currently under the LaneX Holdings umbrella began inside Labs",
      "Home of our design system, shared identity layer and internal SDKs",
      "Testing ground for ideas that could become the next LaneX product",
      "Publishes open experiments and research from time to time",
    ],
  },
  flexit: {
    name: "FlexIt",
    tag: "Financial Technology",
    icon: Wallet,
    tagline: "Money software that works for people — not the other way around.",
    lede: "FlexIt is a financial technology platform helping individuals and small businesses move money, plan spending, unlock credit and grow their financial position with clarity and control.",
    problem: "Personal finance apps are either budgeting toys or scary spreadsheets. Small business finance is worse — a tax portal, a bank, a bookkeeping app, and a whole lot of hope.",
    solution: "FlexIt is one place where you see your money, understand what's happening, plan what's next and act on it — for individuals as a co-pilot, for businesses as a financial back-office.",
    features: [
      { icon: CreditCard,  title: "Move money",     body: "Send, receive and schedule payments across accounts and currencies — with clear fees and instant confirmations." },
      { icon: LineChart,   title: "Plan spending",  body: "Personalised budgets that adjust to your real income and spending — not templates from 2010." },
      { icon: Sparkles,    title: "Smart insights", body: "Every week: what changed, what to watch and one concrete move to strengthen your finances." },
      { icon: Rocket,      title: "Grow credit",    body: "Build a stronger credit profile using data you already have — no gimmicks, no dark patterns." },
      { icon: Building2,   title: "Business tools",  body: "For small businesses: invoicing, expense tracking, cash-flow forecasting and tax-time simplicity." },
      { icon: ShieldCheck, title: "Bank-grade security", body: "Encryption, MFA, and continuous monitoring built on the same LaneX security foundation as our other products." },
    ],
    useCases: [
      "Individuals who want a real financial co-pilot, not another budgeting app",
      "Freelancers and consultants managing irregular income",
      "Small businesses that need one place for money in and money out",
      "Anyone tired of switching between three apps to answer 'can I afford this?'",
    ],
  },
};

// ---------- Shared page component ----------
export default function HoldingsProductPage() {
  const { slug } = useParams();
  const p = PRODUCTS[slug];
  if (!p) return <Navigate to="/about" replace />;
  const Icon = p.icon;

  return (
    <div className="min-h-screen bg-background" data-testid={`holdings-page-${slug}`}>
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-40 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/about" data-testid="holdings-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
            <span className="rounded-md bg-foreground px-2 py-1 font-display text-sm font-black text-background">LaneX</span>
            <span className="font-display text-sm font-bold tracking-tight">Holdings</span>
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/about" className="text-muted-foreground hover:text-foreground transition">All products</Link>
            <a href="mailto:hello@lanexacademy.com" className="text-muted-foreground hover:text-foreground transition">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="grid gap-10 md:grid-cols-[1fr_1.4fr] md:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/5 px-3 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-primary">
                <Icon className="h-3 w-3" /> {p.tag}
              </div>
              <h1 className="mt-6 font-display text-5xl font-black tracking-tighter md:text-6xl leading-[1.05]">
                {p.name}
              </h1>
              <div className="mt-3 font-display text-lg text-primary">{p.tagline}</div>
              <p className="mt-6 text-base text-muted-foreground leading-relaxed">{p.lede}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
                  <a href="mailto:hello@lanexacademy.com"><Mail className="mr-2 h-4 w-4" /> Get early access</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
                  <Link to="/about"><ArrowRight className="mr-2 h-4 w-4" /> Explore the ecosystem</Link>
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              {/* Premium in-app SVG mockup — brand-consistent, no external assets */}
              <div className="group relative rounded-2xl border border-border bg-card p-3 transition hover:border-primary/60 hover:shadow-[0_0_48px_-12px_rgba(0,229,255,0.35)]">
                <div className="pointer-events-none absolute -top-3 -right-3 rounded-full border border-primary/40 bg-background/90 px-3 py-1 font-mono text-[0.6rem] uppercase tracking-widest text-primary shadow-lg">
                  live preview
                </div>
                <div className="aspect-[24/17] w-full overflow-hidden rounded-lg">
                  {MOCKUPS[slug] || (
                    <div className="grid h-full place-items-center text-muted-foreground text-xs font-mono">
                      // preview coming soon
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {p.features.slice(0, 2).map((f) => (
                  <div key={f.title} className="rounded-2xl border border-border bg-card p-4 transition hover:border-primary/60">
                    <div className="grid h-9 w-9 place-items-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
                      <f.icon className="h-4 w-4" />
                    </div>
                    <div className="mt-3 font-display text-sm font-bold tracking-tight">{f.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground leading-relaxed line-clamp-2">{f.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="grid gap-10 md:grid-cols-2">
            <div>
              <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// the problem</div>
              <h2 className="mt-2 font-display text-3xl font-black tracking-tighter md:text-4xl">Why {p.name} exists.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{p.problem}</p>
            </div>
            <div>
              <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// the solution</div>
              <h2 className="mt-2 font-display text-3xl font-black tracking-tighter md:text-4xl">How we solve it.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{p.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// what {p.name.toLowerCase()} does</div>
        <h2 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">A modern, connected platform.</h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {p.features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-6 transition hover:border-primary/60">
              <div className="grid h-10 w-10 place-items-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
                <f.icon className="h-4 w-4" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="grid gap-10 md:grid-cols-[1fr_1.3fr] md:items-start">
            <div>
              <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// built for</div>
              <h2 className="mt-2 font-display text-3xl font-black tracking-tighter md:text-4xl">Who uses {p.name}.</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {p.name} is designed for teams and individuals who care about outcomes, not dashboards.
              </p>
            </div>
            <ul className="space-y-3">
              {p.useCases.map((uc, i) => (
                <li key={i} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
                  <div className="mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-sm">{uc}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Ecosystem callout */}
      <section className="border-t border-border bg-gradient-to-b from-primary/[0.04] to-transparent">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-8 md:p-12">
            <div className="grid gap-6 md:grid-cols-2 md:items-center">
              <div>
                <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// part of the LaneX ecosystem</div>
                <h2 className="mt-2 font-display text-3xl font-black tracking-tighter md:text-4xl">Stronger with every product beside it.</h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  {p.name} shares the same engineering foundation as every other LaneX product — identity, billing, analytics, communications, security. Customers of one product become first-class citizens across the ecosystem.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Button asChild size="lg" className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
                  <a href="mailto:hello@lanexacademy.com"><Mail className="mr-2 h-4 w-4" /> Talk to the team</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
                  <Link to="/about"><ArrowRight className="mr-2 h-4 w-4" /> See every product</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4">
          <div className="font-mono text-xs text-muted-foreground">© {new Date().getFullYear()} LaneX Holdings. All rights reserved.</div>
          <div className="flex items-center gap-4 font-mono text-xs">
            <Link to="/about" className="text-muted-foreground hover:text-foreground transition">LaneX Holdings</Link>
            <a href="https://synclyntech.com" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground transition">SyncLyn</a>
            <a href="mailto:hello@lanexacademy.com" className="text-muted-foreground hover:text-foreground transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
